/**
 * Sector colours for the Atlas Sector lens and the Market tab (Story #6837
 * Stream 9). One colour per ISIC Rev.4 section A–U; anything not classified,
 * or not a section letter, gets the same grey the maps already use for "not one
 * of the things being coloured".
 *
 * The letter is always printed next to the colour (legend, region labels), so
 * colour is never the only way to tell two sections apart — 21 categories is
 * more than any palette can hold at a glance.
 */

/** Same grey as UNKNOWN_PROJECT_TAG_COLOR in lib/whitespace.ts. */
export const UNCLASSIFIED_SECTOR_COLOR = '#94A3B8'

/** ISIC Rev.4 sections, in order. */
export const SECTOR_SECTIONS = [
  'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K',
  'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U',
] as const

export type SectorSection = (typeof SECTOR_SECTIONS)[number]

export const SECTOR_COLORS: Record<SectorSection, string> = {
  A: '#65A30D', // agriculture, forestry, fishing
  B: '#92400E', // mining and quarrying
  C: '#2563EB', // manufacturing
  D: '#F59E0B', // electricity, gas, steam
  E: '#06B6D4', // water supply, waste
  F: '#EA580C', // construction
  G: '#DB2777', // wholesale and retail trade
  H: '#7C3AED', // transport and storage
  I: '#DC2626', // accommodation and food service
  J: '#0D9488', // information and communication
  K: '#15803D', // financial and insurance
  L: '#A16207', // real estate
  M: '#4F46E5', // professional, scientific, technical
  N: '#64748B', // administrative and support
  O: '#9333EA', // public administration, defence
  P: '#CA8A04', // education
  Q: '#E11D48', // human health and social work
  R: '#C026D3', // arts, entertainment, recreation
  S: '#9A3412', // other service activities
  T: '#57534E', // activities of households
  U: '#0369A1', // extraterritorial organisations
}

/** The section letter, upper-cased, when it is one of A–U; otherwise null. */
export function asSection(section: string | null | undefined): SectorSection | null {
  const s = (section ?? '').trim().toUpperCase()
  return (SECTOR_SECTIONS as readonly string[]).includes(s) ? (s as SectorSection) : null
}

export function sectorColor(section: string | null | undefined): string {
  const s = asSection(section)
  return s ? SECTOR_COLORS[s] : UNCLASSIFIED_SECTOR_COLOR
}
